import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth, hasPermission, hasRole } from "./AuthContext";
import { useWSEvent } from "./WebSocketContext";
import { canTallySyncApprove } from "../lib/tallyApproval";
import { onWSConnectionChange } from "../lib/ws";

const TallyApprovalContext = createContext(null);

export function TallyApprovalProvider({ children }) {
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const canApprove = Boolean(user && canTallySyncApprove(user, { hasPermission, hasRole }));
  const userId = user?.id;
  const inflightRef = useRef(null);

  const seedCount = useCallback(() => {
    if (!canApprove) {
      setPendingCount(0);
      return Promise.resolve();
    }
    if (inflightRef.current) return inflightRef.current;
    inflightRef.current = api
      .get("/tally/approval-queue", {
        params: { period: "POST_SEPT", status: "PENDING_APPROVAL", count_only: true },
      })
      .then(({ data }) => {
        setPendingCount(Number(data?.kpis?.pending ?? data?.count ?? 0));
      })
      .catch(() => {
        setPendingCount(0);
      })
      .finally(() => {
        inflightRef.current = null;
      });
    return inflightRef.current;
  }, [canApprove]);

  useEffect(() => {
    seedCount();
  }, [userId, seedCount]);

  useEffect(() => {
    if (!canApprove) return undefined;
    let armed = false;
    return onWSConnectionChange((connected) => {
      if (connected && armed) seedCount();
      if (connected) armed = true;
    });
  }, [canApprove, seedCount]);

  useWSEvent("APPROVAL_QUEUE_COUNT_UPDATE", (event) => {
    if (event?.period && event.period !== "POST_SEPT") return;
    if (typeof event?.pending_count === "number") {
      setPendingCount(event.pending_count);
    }
  });

  const value = useMemo(
    () => ({ pendingCount, setPendingCount, refreshPendingCount: seedCount }),
    [pendingCount, seedCount],
  );

  return <TallyApprovalContext.Provider value={value}>{children}</TallyApprovalContext.Provider>;
}

export function useTallyApproval() {
  const ctx = useContext(TallyApprovalContext);
  if (!ctx) {
    return { pendingCount: 0, setPendingCount: () => {}, refreshPendingCount: () => {} };
  }
  return ctx;
}
